import { ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
  ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR, NgModel } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { QuillModule, QuillModules } from 'ngx-quill';
import Quill, { Delta } from 'quill';
import type { Attributor } from 'parchment';
import MergeFieldBlot from './mergefield.blot';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MergeField, MergeFieldList } from '@k12/models';

interface LinkDataItem {
  text: string;
  url: string;
}

interface QuillImageInsertOp {
  image?: string;
}

interface QuillOp {
  insert?: string | QuillImageInsertOp;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'cfi-richtext',
  templateUrl: './richtext.component.html',
  styleUrl: './richtext.component.scss',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    QuillModule
  ],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CfiRichtextComponent),
      multi: true
    }
  ]
})
export class CfiRichtextComponent implements OnInit, OnDestroy {
  @Input() linkData: LinkDataItem[] = [{ text: '', url: '' }];
  @Input() maxLength: number = 0; // 0 means no limit
  @Input() mergeFieldListArray: MergeFieldList[] = [];
  @Input() editorContent: string = '';
  @Input() isSingleLineEditor: boolean = false; // an indicator for single-line input fields (e.g. email subject lines)
  @Input() uniqueId: string = '';

  @Output() quillEditorOutput: EventEmitter<string> = new EventEmitter<string>();

  currentCharCount: number = 0;
  linkText: string = '';
  linkUrl: string = '';
  quillEditor!: Quill;

  private readonly cd = inject(ChangeDetectorRef);
  private readonly textChangeSubject = new Subject<void>();
  private static mergeFieldBlotRegistered = false;
  private static attributorsRegistered = false;

  constructor() {
    if (!CfiRichtextComponent.mergeFieldBlotRegistered) {
      Quill.register(MergeFieldBlot);
      CfiRichtextComponent.mergeFieldBlotRegistered = true;
    }

    // Register Parchment Style attributors so Quill writes inline styles
    // (email clients strip external styles and class-based styling).
    if (!CfiRichtextComponent.attributorsRegistered) {
      try {
        const Parchment = Quill.import('parchment') as {
          Scope: { BLOCK: unknown; INLINE: unknown };
          StyleAttributor: new (
            name: string,
            keyName: string,
            options: { scope: unknown; whitelist?: string[] }
          ) => Attributor;
          ClassAttributor: new (
            name: string,
            keyName: string,
            options: { scope: unknown; whitelist?: string[] }
          ) => Attributor;
        };
        const StyleAttributor = Parchment.StyleAttributor;
        const ClassAttributor = Parchment.ClassAttributor;

        const AlignStyle = new StyleAttributor('align', 'text-align', { scope: Parchment.Scope.BLOCK });

        // Register class-based size attributor for reading existing class="ql-size-*" formats
        const SizeClass = new ClassAttributor('size', 'ql-size', {
          scope: Parchment.Scope.INLINE,
          whitelist: ['small', 'large', 'huge']
        });

        // Map size values to actual CSS font-size values for inline styles (for writing)
        const SizeStyle = new StyleAttributor('size', 'font-size', {
          scope: Parchment.Scope.INLINE,
          whitelist: ['0.75em', '1.5em', '2.5em']
        });

        Quill.register(SizeClass, true);
        Quill.register(AlignStyle, true);
        Quill.register(SizeStyle, true);
      } catch (err) {
        // If registration fails, do not block editor; email formatting may degrade.
        console.error('Error registering Quill attributors:', err);
      }

      CfiRichtextComponent.attributorsRegistered = true;
    }
  }

  get quillModules(): QuillModules {
    return {
      toolbar: this.isSingleLineEditor ? false : '#' + this.uniqueId,
      keyboard: {
        bindings: {
          tab: {
            key: 'Tab',
            handler: () => true // Or your custom indent logic
          },
          enter: {
            key: 13,
            handler: () => {
              return !this.isSingleLineEditor;
            }
          }
        }
      }
    };
  }

  ngOnInit(): void {
    // Internal Subject; cannot error by construction, so no error handler is needed.
    this.textChangeSubject
      .pipe(
        debounceTime(50) // Wait 100ms after the last change
      )
      .subscribe(() => {
        this.getRawText();
      });
  }

  private onChange: (value: string) => void = () => {};
  private onTouched = () => {};

  writeValue(value: string | null | undefined): void {
    if (this.quillEditor) {
      this.quillEditor.root.innerHTML = value || '';
      this.updateCharacterCount();
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onEditorCreated(editor: Quill): void {
    this.quillEditor = editor;

    if (this.editorContent !== '') {
      // Convert class-based size attributes to inline styles before pasting
      this.editorContent = this.convertQuillClassesToInlineStyles(this.editorContent);

      // Paste HTML into editor
      this.quillEditor.clipboard.dangerouslyPasteHTML(this.editorContent);
    }

    // Prevent the user from entering multi-line text in title/subject line fields. Quill always adds one trailing newline, so allowing only 1 newline effectively makes it single-line text.
    this.quillEditor.on('text-change', (delta: Delta, oldDelta: Delta, source: 'api' | 'user' | 'silent') => {
      if (this.isSingleLineEditor && delta) {
        const text = this.quillEditor.getText();
        const newLineCount = (text.match(/\n/g) || []).length;
        if (newLineCount > 1) {
          this.quillEditor.setContents(oldDelta);
          this.cd.detectChanges();
          this.textChangeSubject.next();
          return;
        }
      }

      if (this.maxLength > 0) {
        this.updateCharacterCount();
      }

      // Prevent the user from pasting or dragging images into the editor. Any image the user tries to insert gets immediately removed. The 'silent' flag prevents this removal from triggering another text-change event.
      if (source === 'user') {
        let index = 0;
        const content = new Delta();
        this.quillEditor.getContents().ops.forEach((op: QuillOp) => {
          if (typeof op.insert !== 'string' && op.insert?.image) {
            content.retain(index);
            content.delete(1);
          } else if (typeof op.insert === 'string') {
            index += op.insert.length;
          }
        });
        if (content.ops.length > 0) {
          this.quillEditor.updateContents(content, 'silent');
        }
      }

      // Check isDelete, when deleting the whole list, check the quill editor does not have any list
      // after deleting (list with one dot/ number)
      // when the character count is 1, remove it. the character count will be zero.
      const isDelete = this.quillEditor.getLines().every((line) => line.length() === 1);
      if (isDelete) {
        this.quillEditor.formatLine(0, 1, {
          list: false
        });
      }
      this.textChangeSubject.next();
    });

    this.quillEditor.on('selection-change', () => {
      this.textChangeSubject.next();
    });

    this.quillEditor.on('paste', () => {
      setTimeout(() => {
        this.textChangeSubject.next();
      }, 10);
    });

    this.quillEditor.on('format-change', () => {
      this.textChangeSubject.next();
    });

    // Ensure preloaded content in edit mode immediately updates form value and character count
    // without requiring the user to focus or type in the editor first.
    this.getRawText();
    this.cd.detectChanges();
  }

  insertMergeField(item: MergeField): void {
    const range = this.quillEditor.getSelection(true);
    if (range) {
      this.quillEditor.insertEmbed(range.index, 'mergeField', item, 'user');

      const newIndex = range.index + item.placeHolder.length;

      // Set focus to the editor after the toolbar menu closes when inserting merge field
      setTimeout(() => {
        this.quillEditor.focus();
        this.quillEditor.setSelection(newIndex, 0);
      }, 0);
    }
  }

  isValidLinkUrl(): boolean {
    return CfiRichtextComponent.isValidUrl(this.linkUrl);
  }

  insertLink(linkUrlModel?: NgModel, linkTextModel?: NgModel): void {
    if (!this.linkText.trim() || !this.isValidLinkUrl()) {
      return; // Don't insert empty text or invalid/malformed URLs
    }

    const range = this.quillEditor.getSelection(true) ?? { index: this.quillEditor.getLength(), length: 0 };
    const delta = new Delta()
      .retain(range.index)
      .delete(range.length ?? 0)
      .insert(this.linkText, { link: this.linkUrl });
    this.quillEditor.updateContents(delta, 'user');
    this.quillEditor.setSelection(range.index + this.linkText.length, 0);

    // Reset the form after insertion
    this.linkText = '';
    this.linkUrl = '';

    // Reset touched/dirty state so the fields don't render as invalid the next time the menu opens
    linkUrlModel?.control.markAsUntouched();
    linkUrlModel?.control.markAsPristine();
    linkTextModel?.control.markAsUntouched();
    linkTextModel?.control.markAsPristine();
  }

  getRawText(): void {
    if (this.quillEditor) {
      let editorContent: string;

      // Strip <p> tags for title inputs (email subject lines shouldn't have HTML tags)
      if (this.isSingleLineEditor) {
        editorContent = this.quillEditor.root.innerHTML.replace(/<\/?p>/g, '');
      } else {
        // getSemanticHTML() drops block-level attributes (e.g. text-align) from li elements
        // so use root.innerHTML instead which preserves all inline styles
        editorContent = CfiRichtextComponent.stripQuillUiElements(this.quillEditor.root.innerHTML);
      }
      this.updateCharacterCount();
      this.emitEditorValues(editorContent);
      this.cd.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.textChangeSubject.complete();
  }

  private static stripQuillUiElements(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    doc.querySelectorAll('.ql-ui').forEach((el) => el.remove());
    return doc.body.innerHTML;
  }

  private static isValidUrl(value: string): boolean {
    try {
      const url = new URL(value.trim());
      return url.protocol === 'http:' || url.protocol === 'https:' || url.protocol === 'mailto:';
    } catch {
      return false;
    }
  }

  private convertQuillClassesToInlineStyles(html: string): string {
    const sizeMap: Record<string, string> = {
      'ql-size-small': '0.75em',
      'ql-size-large': '1.5em',
      'ql-size-huge': '2.5em'
    };

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    Object.entries(sizeMap).forEach(([className, fontSize]) => {
      doc.querySelectorAll(`.${className}`).forEach((element: Element) => {
        element.classList.remove(className);
        const htmlElement = element as HTMLElement;
        const existingStyle = htmlElement.style.cssText;
        htmlElement.style.cssText = existingStyle
          ? `${existingStyle}; font-size: ${fontSize}`
          : `font-size: ${fontSize}`;
      });
    });

    return doc.body.innerHTML;
  }

  private updateCharacterCount(): void {
    const textLength = this.quillEditor.getText().replace(/\n$/, '').length;
    this.currentCharCount = Math.max(textLength, 0);

    if (textLength > this.maxLength) {
      this.quillEditor.deleteText(this.maxLength, textLength);
      this.currentCharCount = this.maxLength;
    }
  }

  private emitEditorValues(editorContent: string): void {
    const quillText = this.quillEditor.getText().replace(/\n$/, '');
    const validationText = this.getValidationText(editorContent, quillText);

    if (validationText.length === 0) {
      this.onChange(validationText);
      this.quillEditorOutput.emit('');
    } else {
      this.onChange(validationText);
      this.quillEditorOutput.emit(editorContent);
    }
  }

  private getValidationText(editorContent: string, quillText: string): string {
    if (quillText.length > 0) {
      return quillText;
    }

    // Merge fields are Quill embeds and may not be reflected in getText(),
    // so derive fallback text from rendered HTML for reactive-form validation.
    const parser = new DOMParser();
    const doc = parser.parseFromString(editorContent, 'text/html');
    return doc.body.textContent || '';
  }
}
